import React, { useEffect, useState } from "react";

import PartyIcon from "@common/assets/images/party-icon.png";
import { getPersistedItem, setPersistedItem } from "@common/util/localStorage";

import { FeedbackRowItem, InterjectedRowType, RecommendUsRowItem } from "./InterjectedRow.model";
import * as Styled from "./InterjectedRows.styles";

const FEEDBACK_TEXT_VARIANTS = [
  { id: "team_value", text: "What's the #1 thing that would make Shake more useful for your team?" },
  { id: "annoying", text: "What's annoying about Shake you wish we'd fix?" },
  { id: "build_next", text: "What should we build next?" },
  { id: "wish_feature", text: "What's one thing you wish Shake could do that it can't today?" },
];

/// `{n}` is replaced with the milestone's ticket count.
const RECOMMEND_US_VARIANTS = [
  {
    id: "in_the_books",
    title: "{n} tickets in the books.",
    description: "If Shake's been useful, a quick mention to a friend would mean the world to us.",
  },
  {
    id: "and_counting",
    title: "{n} reports and counting!",
    description: "We grow through word-of-mouth and we’d appreciate a recommendation.",
  },
  {
    id: "collected",
    title: "{n} tickets collected!",
    description: "If you appreciate the tool, a simple mention to a fellow tech lead is the best support we can get.",
  },
  {
    id: "handled",
    title: "{n} tickets handled.",
    description: "If you think Shake deserves it, please tell a friend about us.",
  },
];

const TRIGGER_STEP = 100;
const COOLDOWN_MS = 70 * 24 * 60 * 60 * 1000; // 70 days

const stateKey = (appId: string) => `shakebugs.interjectionCadence.${appId}`;

interface FiredMilestone {
  /// Multiply by TRIGGER_STEP for the actual ticket-count milestone (e.g. threshold 2 -> 200).
  threshold: number;
  /// rotationIndex at the moment this one fired - `% 3 === 0` means it was "What's missing",
  /// otherwise "Recommend us". Frozen per-milestone so earlier rows keep their original type even
  /// as rotationIndex keeps advancing for later ones.
  rotationIndexAtFire: number;
  /// Index into FEEDBACK_TEXT_VARIANTS or RECOMMEND_US_VARIANTS, depending on the row type. Null on
  /// milestones stored before recommend-us had variants (falls back to the first one).
  variantIndex: number | null;
}

interface CadenceState {
  /// Every milestone that has actually fired a row - each stays in this list (and therefore stays
  /// visible, until the caller filters out a dismissed id) rather than being replaced by the next
  /// one. A milestone skipped during another one's cooldown never enters this list at all.
  firedMilestones: FiredMilestone[];
  /// The highest threshold a trigger has fired for - gates whether a new one is due.
  lastTriggerThreshold: number;
  lastTriggerAt: number | null;
  /// Count of rows fired so far - `rotationIndex % 3 === 0` picks "What's missing", otherwise
  /// "Recommend us", giving the spec's 1/3 : 2/3 split.
  rotationIndex: number;
}

const DEFAULT_STATE: CadenceState = {
  firedMilestones: [],
  lastTriggerThreshold: 0,
  lastTriggerAt: null,
  rotationIndex: 0,
};

/**
 * The "What's missing" / "Recommend us" placement formula for the User Feedback list - triggers
 * every 100 tickets for the current app, with a 70-day cooldown and a 1-in-3 rotation onto
 * "What's missing". Entirely client-side and permanent: once a milestone fires, its row keeps
 * being returned (for the caller to show, until the user dismisses it) rather than disappearing
 * on its own - and every past milestone that fired stays visible alongside newer ones, not just
 * the most recent.
 *
 * A milestone crossed during another milestone's cooldown is simply skipped - nothing is queued,
 * so the next eligible check (using the then-current ticket total) is what actually fires.
 */
export function useInterjectionCadence(appId: string | undefined, total: number) {
  const [rows, setRows] = useState<(FeedbackRowItem | RecommendUsRowItem)[]>([]);

  useEffect(() => {
    if (!appId) {
      setRows([]);
      return;
    }

    const state = readState(appId);
    const currentThreshold = Math.floor(total / TRIGGER_STEP);
    const withinCooldown = state.lastTriggerAt !== null && Date.now() - state.lastTriggerAt < COOLDOWN_MS;
    const shouldFire = currentThreshold > state.lastTriggerThreshold && !withinCooldown;

    const effectiveState = shouldFire ? fireTrigger(appId, state, currentThreshold) : state;
    setRows(buildRows(appId, effectiveState, total));
  }, [appId, total]);

  return rows;
}

function readState(appId: string): CadenceState {
  const stored = getPersistedItem(stateKey(appId));

  if (!stored || !Array.isArray(stored.firedMilestones)) return DEFAULT_STATE;

  return stored;
}

function fireTrigger(appId: string, state: CadenceState, currentThreshold: number): CadenceState {
  const isFeedback = state.rotationIndex % 3 === 0;

  const firedMilestone: FiredMilestone = {
    threshold: currentThreshold,
    rotationIndexAtFire: state.rotationIndex,
    variantIndex: Math.floor(Math.random() * (isFeedback ? FEEDBACK_TEXT_VARIANTS : RECOMMEND_US_VARIANTS).length),
  };

  const next: CadenceState = {
    firedMilestones: [...state.firedMilestones, firedMilestone],
    lastTriggerThreshold: currentThreshold,
    lastTriggerAt: Date.now(),
    rotationIndex: state.rotationIndex + 1,
  };

  setPersistedItem(stateKey(appId), next);
  return next;
}

function buildRows(appId: string, state: CadenceState, total: number): (FeedbackRowItem | RecommendUsRowItem)[] {
  return state.firedMilestones.map((fired) => {
    const milestone = fired.threshold * TRIGGER_STEP;
    const isFeedback = fired.rotationIndexAtFire % 3 === 0;

    const positionFromBottom = milestone + 1;
    const afterIndex = total - positionFromBottom;

    if (isFeedback) {
      const variant = FEEDBACK_TEXT_VARIANTS[fired.variantIndex ?? 0] ?? FEEDBACK_TEXT_VARIANTS[0];

      return {
        id: `feedback-${appId}-${milestone}`,
        type: InterjectedRowType.FEEDBACK,
        after_index: afterIndex,
        placeholder: variant.text,
        variant_id: variant.id,
        action_label: "Submit feedback",
      };
    }

    const variant = RECOMMEND_US_VARIANTS[fired.variantIndex ?? 0] ?? RECOMMEND_US_VARIANTS[0];

    return {
      id: `recommend_us-${appId}-${milestone}`,
      type: InterjectedRowType.RECOMMEND_US,
      after_index: afterIndex,
      icon: (
        <Styled.Icon
          src={PartyIcon}
          alt=""
        />
      ),
      variant_id: variant.id,
      title: variant.title.replace("{n}", String(milestone)),
      description: variant.description,
      action_label: "Share Shake",
    };
  });
}
